import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: "error" | "info" | "success";
  context?: string;  // extra context for feedback reports
  action?: ToastAction;  // inline action button (e.g. "Undo")
}

export interface ToastOptions {
  context?: string;
  action?: ToastAction;
  ttl?: number;  // override default TTL in ms
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type?: Toast["type"], optsOrContext?: string | ToastOptions) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType>({
  toasts: [],
  addToast: () => {},
  dismissToast: () => {},
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((
    message: string,
    type: Toast["type"] = "info",
    optsOrContext?: string | ToastOptions,
  ) => {
    const opts: ToastOptions = typeof optsOrContext === "string"
      ? { context: optsOrContext }
      : (optsOrContext || {});
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type, context: opts.context, action: opts.action }]);
    // Error toasts stay longer so user can click "Report this".
    // Toasts with actions stay longer so user has time to undo.
    const defaultTtl = type === "error" ? 8000 : opts.action ? 6000 : 4000;
    const ttl = opts.ttl ?? defaultTtl;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ttl);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
